// Helpers para os <iframe> que o produtor cola no HTML de uma aula tipo Texto
// (Thinglink, YouTube, Google Forms, players diversos). O HTML é conteúdo de
// terceiro: a src pode vir vazia, relativa ao protocolo ou com um esquema que
// não deve ser navegado, e as dimensões vêm em formatos variados.

export const DEFAULT_EMBED_ASPECT_RATIO = 16 / 9
export const MIN_EMBED_HEIGHT = 180

const HTTP_PROTOCOL = /^http:\/\//i
const HTTPS_PROTOCOL = /^https:\/\//i

/**
 * Normaliza a `src` de um embed colado para uma URL https navegável, ou `null`
 * quando não há nada seguro para renderizar.
 *
 * - `//host/path` (protocol-relative, comum em embeds antigos) recebe `https:`.
 * - `http://` é promovido a `https://`: a área de membros roda em https, então
 *   um iframe http seria bloqueado como mixed content de qualquer forma.
 * - Qualquer outro esquema (`javascript:`, `data:`, `about:`, relativo) é
 *   descartado — não é embed de vídeo e navegar nele dentro do app é risco.
 */
export function normalizeEmbedSrc(src?: string | null): string | null {
  const trimmed = src?.trim()
  if (!trimmed) return null

  const absolute = trimmed.startsWith('//') ? `https:${trimmed}` : trimmed

  if (HTTPS_PROTOCOL.test(absolute)) return absolute
  if (HTTP_PROTOCOL.test(absolute)) {
    return absolute.replace(HTTP_PROTOCOL, 'https://')
  }

  return null
}

const parseLength = (value?: string | null): number | null => {
  if (!value) return null
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(?:px)?$/i)
  if (!match) return null
  const parsed = Number(match[1])
  return parsed > 0 ? parsed : null
}

const parseStyleLength = (
  style: string | undefined,
  property: 'width' | 'height',
): number | null => {
  if (!style) return null
  const match = style.match(
    new RegExp(`(?:^|;)\\s*${property}\\s*:([^;]+)`, 'i'),
  )
  return parseLength(match?.[1])
}

const intrinsicLength = (
  attributes: Record<string, string>,
  property: 'width' | 'height',
): number | null =>
  parseLength(attributes[property]) ??
  parseStyleLength(attributes.style, property) ??
  parseLength(attributes[`data-original-${property}`])

/**
 * Calcula o tamanho de renderização do embed: sempre preenche a largura
 * disponível (o embed vira o "player" da aula) e deriva a altura da proporção
 * declarada no HTML colado — 16:9 quando o produtor não declarou nada útil
 * (ex.: `width="100%"`).
 */
export function resolveEmbedSize(
  attributes: Record<string, string>,
  availableWidth: number,
): { width: number; height: number } {
  const declaredWidth = intrinsicLength(attributes, 'width')
  const declaredHeight = intrinsicLength(attributes, 'height')

  const aspectRatio =
    declaredWidth && declaredHeight
      ? declaredWidth / declaredHeight
      : DEFAULT_EMBED_ASPECT_RATIO

  const width = Math.max(1, Math.floor(availableWidth))
  const height = Math.max(MIN_EMBED_HEIGHT, Math.round(width / aspectRatio))

  return { width, height }
}
